import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { findChild } from "shared/lib/treeUtils"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { ApiResponse } from "shared/types/ApiResponse"
import { BatchCreateableProductPosition } from "shared/types/ProductPosition"
import {
  MinimalProject,
  ProjectTypename,
  RoomTypename,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"

import { useBatchCreateProductPositions } from "entities/product-positions/api/useBatchCreateProductPositions"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import { useProjectOptions } from "entities/projects/model/useProjectOptions"
import { useProjectPositionOptions } from "entities/projects/model/useProjectPositionOptions"
import { useRoomOptions } from "entities/rooms/model/useRoomOptions"
import { MinimalRoom } from "entities/rooms/types/Room"

import { CopyProjectChildrenOverlayModel } from "../CopyProjectChildrenOverlay"
import { CopyProjectChildrenOverlayConnectorProps } from "../CopyProjectChildrenOverlayConnector"
import { CopyProjectChildrenFormData } from "../CopyProjectChildrenOverlayView"

export function useCopyProjectChildrenOverlayModel({
  payload
}: CopyProjectChildrenOverlayConnectorProps): CopyProjectChildrenOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { t } = useTranslation()
  const { projectID: sourceProjectID, projectChildren } = payload
  const [fetchProject] = useProjectChildrenLazyQuery()
  const [batchCreateProductPositions] = useBatchCreateProductPositions()
  const { activeProjectID } = useNeoletterUserProfile()

  const productPositions = useMemo(
    () => projectChildren.filter(isProjectProductPosition),
    [projectChildren]
  )
  const rooms = useMemo(
    () => projectChildren.filter(isProjectRoom),
    [projectChildren]
  )

  const isOnlyProductPositions =
    productPositions.length > 0 && rooms.length === 0
  const isOnlyRooms = productPositions.length === 0 && rooms.length > 0

  const headline = useMemo(() => {
    if (isOnlyProductPositions) {
      return t("ProductPosition.MultiActions.Copy")
    }
    if (isOnlyRooms) {
      return t("Room.MultiActions.Copy")
    }
    return undefined
  }, [isOnlyProductPositions, isOnlyRooms, t])

  const defaultFormData: CopyProjectChildrenFormData = useMemo(
    () => ({
      projectID: activeProjectID === sourceProjectID ? null : activeProjectID,
      parentID: activeProjectID === sourceProjectID ? null : activeProjectID,
      position: 0
    }),
    [activeProjectID, sourceProjectID]
  )

  const [formData, setFormData] = useState(defaultFormData)

  const { options: projectOptions, isLoading: isLoadingProjects } =
    useProjectOptions({ excludedProjectIDs: sourceProjectID })

  const { options: parentOptions, isLoading: isLoadingParentOptions } =
    useRoomOptions(formData.projectID)

  const { positionOptions, isLoading: isLoadingPositionOptions } =
    useProjectPositionOptions({
      projectID: formData.projectID,
      parentID: formData.parentID,
      childType: RoomTypename,
      childID: null
    })

  const submitCopy = useEvent(
    async (
      submittedFormData: CopyProjectChildrenFormData
    ): Promise<ApiResponse<unknown>> => {
      const { projectID, parentID, position } = submittedFormData

      if (!projectID) {
        return { data: null, errors: [{ message: "No Project Selected" }] }
      }
      const { data } = await fetchProject({ variables: { id: projectID } })

      if (!data?.project) {
        return { data: null, errors: [{ message: "Target Project Not Found" }] }
      }

      const { project } = data

      const parent =
        (findChild(parentID, project.children) as
          | MinimalProject
          | MinimalRoom) || project

      const batchCreateableProductPositions: BatchCreateableProductPosition[] =
        productPositions.map((productPosition, index) => ({
          ...productPosition,
          parent: {
            id: parent?.id,
            type: parent?.__typename || ProjectTypename
          },
          position: position + index
        }))

      const result = await batchCreateProductPositions({
        projectID,
        productPositions: batchCreateableProductPositions
      })
      return {
        data: result?.data ?? null,
        errors: result?.errors?.map(error => ({ message: error.message }))
      }
    }
  )

  const { handleSubmit, isSubmitting } =
    useFormSubmit<CopyProjectChildrenFormData>({
      onSubmit: submitCopy,
      onSuccess: closeOverlay
    })

  const isLoadingPositions = isLoadingParentOptions || isLoadingPositionOptions

  return {
    projectOptions,
    isLoadingProjects,
    isLoadingPositions,
    defaultFormData,
    parentOptions,
    positionOptions,
    headline,
    onFormDataChange: setFormData,
    onSubmit: handleSubmit,
    onCancel: closeOverlay,
    isSubmitting
  }
}
